const suffixMap = {
  _id: 'ID',
  _url: 'URL',
  _uuid: 'UUID',
} as const;

type SuffixMap = typeof suffixMap;
type KnownSuffix = keyof SuffixMap;
type CapitalizeSnake<S extends string> = S extends `${infer Head}_${infer Tail}`
  ? `${Head}${CapitalizeSnake<Capitalize<Tail>>}`
  : S;

type TransformKey<K extends string> = {
  [S in KnownSuffix]: K extends `${infer P}${S}` ? `${CapitalizeSnake<P>}${SuffixMap[S]}` : never;
}[KnownSuffix] extends infer T
  ? T extends string
    ? T
    : CapitalizeSnake<K>
  : never;

type TransformObject<T> = {
  [K in keyof T as K extends string ? TransformKey<K> : K]: T[K];
};

export class AnalyticsKeyTransformer {
  private static readonly keyCache = new Map<string, string>();

  /** Convert prefix to camelCase (snake_case → lowerCamelCase) */
  private static camelizePrefix(prefix: string): string {
    return prefix
      .split('_')
      .filter(Boolean)
      .map((part, i) => (i === 0 ? part.toLowerCase() : part[0].toUpperCase() + part.slice(1).toLowerCase()))
      .join('');
  }

  /** Convert a single key to camelCase + suffix rules */
  private static toCamelKey(key: string): string {
    if (this.keyCache.has(key)) {
      const cached = this.keyCache.get(key);
      if (cached !== undefined) {
        return cached;
      }
      // fallback, should not happen as we just checked has(key)
      return '';
    }

    for (const suffix of Object.keys(suffixMap).sort((a, b) => b.length - a.length) as Array<KnownSuffix>) {
      if (key.toLowerCase().endsWith(suffix.toLowerCase())) {
        const prefix = key.slice(0, -suffix.length);
        const camelPrefix = this.camelizePrefix(prefix);
        const newKey = camelPrefix + suffixMap[suffix];
        this.keyCache.set(key, newKey);
        return newKey;
      }
    }

    const newKey = this.camelizePrefix(key);
    this.keyCache.set(key, newKey);
    return newKey;
  }

  /** Public API — converts all keys of a flat object */
  public static translateKeys<T extends Record<string, any>>(obj: T): TransformObject<T> {
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => [this.toCamelKey(k), v])) as TransformObject<T>;
  }
}
